// Style
import mainStyle from "./mainStyle.module.scss";

// Packages
import React, { useState, useEffect, useMemo } from "react";
import { cloneDeep } from "lodash";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

// MUI
import { useTheme } from "@mui/material/styles";
import {
  Divider,
  Grid,
  Box,
  Snackbar,
  Alert
} from "@mui/material";

// Components
import { SolidButton } from "components/buttons/SolidButton";
import TableField from "components/Table";
import ActionSelector from "./components/ActionSelector";
import DevicesModal from "./components/DevicesModal";
import ReleaseNotesModal from "./components/ReleaseNotesModal";
import SupportListModal from "./components/SupportListModal";
import PendingChildren from './components/PendingChildren';
import SearchInput from "components/SearchInput";
import ColumnFilterToggle from "components/Table/ColumnFilterToggle";

// Hook
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

// Helper
import { getMainSendRequestParams, initTableData } from "./helper";

// Img
import { ReactComponent as SupportListIcon } from "assets/images/icons/icn_support_list_light.svg";

// Const
import { getMainTableColumns } from "./const";

const FirmwareManagements = () => {
  const [uuids] = useState(() => ({
    main: uuidv4(),
    actionSelector: {
      updateNow: () => uuidv4(),
      scheduleUpdate: () => uuidv4(),
      cancelUpdate: () => uuidv4(),
    },
  }));

  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const { auth, isAuthReady } = useAuthScope();

  const theme = useTheme();
  const columnHelper = createColumnHelper();

  // Message box
  const [isError, setIsError] = useState(false);
  const [messages, setMessages] = useState({
    success: null,
    error: null,
    warning: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const isEmptyTable = useMemo(() => !tableData?.length, [tableData?.length]);
  const [isShowActionButton, setIsShowActionButton] = useState(false);

  // Device modal
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [isOpenDeviceModal, setIsOpenDeviceModal] = useState(false);

  // Target FW Ver modal
  const [selectedFW, setSelectedFW] = useState(null);
  const [isOpenReleaseModal, setIsOpenReleaseModal] = useState(false);

  // Support List
  const [isOpenSupportListModal, setIsOpenSupportListModal] = useState(false);
  const advancedFilterDataList = useMemo(() => {
    return [(auth?.scopeLevel !== "site" && "siteName"), "deviceType", "seriesName", "firmwareAvailabilitiesLabel"].filter(Boolean)
  }, [auth?.scopeLevel]);

  const handelSelectedDevices = devices => {
    setSelectedDevices(cloneDeep(devices));
    setIsOpenDeviceModal(true);
  }

  const handelSelectedTargetFwVer = fw => {
    setSelectedFW(fw);
    setIsOpenReleaseModal(true);
  }

  const mainTableInstance = useTableInstance({
    tableName: "FirmwareManagements",
    data: tableData,
    columns: getMainTableColumns({
      auth,
      columnHelper,
      permissions: auth.permissions,
      theme,
      callbacks: {
        handelSelectedDevices,
        handelSelectedTargetFwVer
      }
    }),
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: ["seriesName"],
    enableSorting: true,
    hiddenColumns: [],
    enableColumnOrder: false,
    enableRowSelection: true,
    enablePagination: true,
  });

  const selectedFWInstancesList = useMemo(() => {
    return mainTableInstance && mainTableInstance.table.getSelectedRowModel().rows
  }, [mainTableInstance?.table?.getSelectedRowModel()?.rows]);

  const fetchTableData = () => {
    if (isAuthReady) {
      setIsLoading(true);

      const { apiKey, restfulParams } = getMainSendRequestParams({ auth });

      sendRequest(
        apiKey,
        uuids.main,
        restfulParams
      );

      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchTableData();
  }, [isAuthReady]);

  useEffect(() => {
    const response = getData(uuids.main);
    const isRespError = !!response?.error || response?.code >= 400;
    const isRespSuccess = !!response?.data || !!response?.result;

    if (isRespError) {
      setIsLoading(false);
      setIsError(true);
      setMessages({
        ...messages,
        error: response?.message || ""
      });

      return;
    }

    if (isRespSuccess) {
      const responseData = response.data || response.result;
      const tmpTableData = initTableData({
        responseData
      });

      setTableData(tmpTableData);
      setIsLoading(false);
    }

  }, [dataMap])

  return (
    <Box className={`main-card-content ${isEmptyTable ? "main-card-content__empty_table" : ""}`}>
      <Box>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Error message box */}
          <Snackbar
            open={isError}
            autoHideDuration={3000}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            onClose={() => setIsError(false)}
            TransitionProps={{
              onExited: () => setMessages(prev => ({ ...prev, error: null }))
            }}
          >
            <Alert
              onClose={() => setIsError(false)}
              severity="error" sx={{ width: "100%" }}
            >
              {messages?.error || ""}
            </Alert>
          </Snackbar>

          <Box sx={{ height: "40px" }}>
            {
              selectedFWInstancesList?.length > 0 && (
                <Grid container sx={{ minHeight: "100%" }} alignItems="center">
                  <ActionSelector
                    {...{
                      isError,
                      setIsError,
                      messages,
                      setMessages,
                      mainTableInstance,
                      isShowActionButton,
                      setIsShowActionButton,
                      selectedFWInstancesList,
                      initCallback: () => {
                        fetchTableData();
                      }
                    }}
                  />
                  <Divider
                    sx={{ width: "1px", margin: "0 0 0 20px" }}
                    orientation="vertical"
                    flexItem
                  />
                </Grid>
              )
            }

          </Box>

          <Box sx={{ height: "80px" }}>
            <Grid container sx={{ minHeight: "100%" }} alignItems="center" gap={1.5}>
              <Divider
                sx={{ width: "1px", height: "40px", margin: "20px 20px 0" }}
                orientation="vertical"
                flexItem
              />
              {/* Search */}
              <SearchInput
                value={mainTableInstance.globalFilter}
                onChange={mainTableInstance.setGlobalFilter}
                placeholder="Search by Name"
              />
              {/* Advanced Search */}
              <ColumnFilterToggle
                {...mainTableInstance}
                filterData={advancedFilterDataList}
              />
              {/* Support List Button */}
              <SolidButton
                startIcon={<SupportListIcon />}
                width="130px"
                height="32px"
                onClick={() => setIsOpenSupportListModal(true)}
              >
                Support List
              </SolidButton>
            </Grid>
          </Box>

        </Grid>

        <TableField
          {...mainTableInstance}
          pendingChildren={
            <PendingChildren
              {...{
                isLoading,
                list: tableData,
                // description: "No data"
              }}
            />
          }
        />

      </Box>

      {
        isOpenDeviceModal && selectedDevices?.[0] &&
        <DevicesModal
          {...{
            selectedDevices,
            setSelectedDevices,
            isOpenDeviceModal,
            setIsOpenDeviceModal,
          }}
        />
      }

      {
        isOpenSupportListModal &&
        <SupportListModal
          {...{
            isError,
            setIsError,
            messages,
            setMessages,
            tableData,
            selectedFW,
            setSelectedFW,
            isOpenSupportListModal,
            setIsOpenSupportListModal,
            setIsOpenReleaseModal
          }}
        />
      }

      {
        isOpenReleaseModal && selectedFW &&
        <ReleaseNotesModal
          {...{
            isError,
            setIsError,
            messages,
            setMessages,
            selectedFW,
            setSelectedFW,
            isOpenReleaseModal,
            setIsOpenReleaseModal,
            isOpenSupportListModal
          }}
        />
      }

    </Box>
  );
};

export default FirmwareManagements;
